import Link from 'next/link';
import {useRouter} from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import {useGlobalFulfillmentDetail} from '@/components/global/providers/GlobalFulfillmentDetailProvider';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import {EzcaterLogo} from '@/components/ui';
import useIdentity from '@/hooks/useIdentity';
import usePersonalizedHomePage from '@/hooks/usePersonalizedHomePage';
import {HOME_PATH, SEARCH_NEW_PATH} from '@/paths';
import {compilePath} from '@/utils';
import {FulfillmentDetailResultToStartNewSearchAddressParams} from '@/utils/fulfillmentDetailResult';
import {PersonalizedHomePageVariant} from '@/utils/personalizedHomePage';
import useNavLinkTracking from '../hooks/useNavlinkTracking';

type LogoLinkProps = {
  href?: string;
  onClick?: () => void;
};

const LogoLink: React.FC<LogoLinkProps> = ({href, onClick}) => {
  const {t} = useTranslation('app-bar');

  const {fulfillmentDetail} = useGlobalFulfillmentDetail();
  const fulfillmentDetailAddress = fulfillmentDetail;
  const {trackLink} = useNavLinkTracking();
  const router = useRouter();
  const currentPath = router.pathname;

  const {isOnCuisineMetroPage, isOnMetroPage, isOnLoggedOutLandingPage} = useStoreNextRouter();

  const city = Array.isArray(router.query.city) ? router.query.city[0] : router.query.city;
  const state = Array.isArray(router.query.state) ? router.query.state[0] : router.query.state;

  const hasMetroLocation = (isOnCuisineMetroPage || isOnMetroPage) && city && state;

  const address = fulfillmentDetailAddress
    ? FulfillmentDetailResultToStartNewSearchAddressParams(fulfillmentDetailAddress)
    : hasMetroLocation
      ? {'address[city]': city, 'address[state]': state}
      : {};

  const trackLogoClick = () => {
    trackLink({path: currentPath, title: 'logo'});
    onClick?.();
  };
  const searchPath = compilePath(
    SEARCH_NEW_PATH,
    {},
    {
      ...address,
    },
    {skipEmptyString: true, skipNull: true},
  );

  const logoFillColor = () => {
    if (isOnLoggedOutLandingPage) return 'fill-green-300';
    return;
  };

  return (
    <Link
      aria-label={t('logo.title')}
      href={href ?? searchPath}
      className="relative inline-block shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-[2.5px] focus-visible:ring-peppercorn-800 focus-visible:ring-offset-1"
      onClick={trackLogoClick}
    >
      <div className="hidden appbar-md:block">
        <EzcaterLogo variant="lockup" fillClassName={logoFillColor()} />
      </div>

      <div className="block appbar-md:hidden">
        <EzcaterLogo variant="symbol-sm" fillClassName={logoFillColor()} />
      </div>
    </Link>
  );
};

/**
 * ezx570::Discovery::PersonalizedHomePage — treatment sends the logo to /home, control keeps the
 * search link. Only logged-in users are eligible, so the flag is only evaluated in here.
 *
 * The arms diverge on the click rather than on mount, so exposure fires on the click for both
 * arms (treatment fires again when /home mounts, control's search page has no exposure of its
 * own). The logo renders on pages that never assign server-side, so it logs the assignment
 * client-side — without it, control users who click from those pages would have an exposure but
 * no assignment and be dropped from the analysis, while treatment gets assigned on /home.
 */
const PersonalizedHomePageLogo: React.FC<unknown> = () => {
  const {trackExperimentExposed, variation} = usePersonalizedHomePage({logAssignment: true});

  return (
    <LogoLink
      href={
        variation === PersonalizedHomePageVariant.TREATMENT ? compilePath(HOME_PATH) : undefined
      }
      onClick={trackExperimentExposed}
    />
  );
};

const Logo: React.FC<unknown> = () => {
  const {isLoggedIn} = useIdentity();

  return isLoggedIn ? <PersonalizedHomePageLogo /> : <LogoLink />;
};

export default Logo;
